import { Button } from '@/components/ui/button'
import { appConfig } from '@/config/appConfig'
import { useAuthStore } from '@/store/auth'
import { useOrganizationStore } from '@/store/organization'
import { AlertCircle, ArrowRight, Code, Mail } from 'lucide-react'
import { useState } from 'react'

/**
 * Branding sidebar component
 */
const BrandingSidebar = ({ description }: { description: string }) => {
  return (
    <div className="hidden flex-col justify-center items-center p-12 lg:flex lg:w-1/2 bg-primary/10">
      <div className="max-w-xl">
        <Code size={64} className="mb-8 text-primary" />
        <h1 className="mb-4 text-4xl font-bold">Sully.ai API Dashboard</h1>
        <p className="mb-6 text-xl text-muted-foreground">{description}</p>

        <div className="mt-8 space-y-4">
          <h3 className="text-xl font-semibold">
            Once activated, you&apos;ll have access to:
          </h3>
          <ul className="space-y-3">
            <li className="flex items-center">
              <div className="p-1 mr-3 rounded-full bg-primary/20">
                <ArrowRight size={16} className="text-primary" />
              </div>
              <span>Secure API key management</span>
            </li>
            <li className="flex items-center">
              <div className="p-1 mr-3 rounded-full bg-primary/20">
                <ArrowRight size={16} className="text-primary" />
              </div>
              <span>Webhook configuration</span>
            </li>
            <li className="flex items-center">
              <div className="p-1 mr-3 rounded-full bg-primary/20">
                <ArrowRight size={16} className="text-primary" />
              </div>
              <span>Usage analytics & billing</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * Organization pending approval view
 */
export const PendingApprovalView = () => {
  const signOut = useAuthStore((s) => s.signOut)
  const organization = useOrganizationStore((s) => s.organization)
  const [contactFormSubmitted, setContactFormSubmitted] =
    useState<boolean>(false)

  const {
    company: { salesEmail },
    features: {
      organization: { activationEmailSubject },
    },
    ui: { accountAccess },
  } = appConfig

  const handleContactSales = () => {
    const subject = encodeURIComponent(activationEmailSubject)
    const body = encodeURIComponent(
      `Hello,\n\nI would like to request activation for my organization (${organization?.name || 'Name not available'}).\n\nThank you.`,
    )

    window.open(
      `mailto:${salesEmail}?subject=${subject}&body=${body}`,
      '_blank',
    )
    setContactFormSubmitted(true)
  }

  const handleSignOut = () => {
    signOut({ redirectUrl: window.location.origin })
  }

  return (
    <div className="flex w-full h-screen bg-background">
      <BrandingSidebar description={accountAccess.pendingDescription} />

      {/* Right panel - Waiting information */}
      <div className="flex flex-col justify-center items-center p-6 w-full lg:w-1/2 md:p-12">
        <div className="space-y-8 w-full max-w-md">
          <div className="flex items-start space-x-4">
            <AlertCircle className="mt-1 w-6 h-6 text-amber-500" />
            <div>
              <h2 className="text-3xl font-bold tracking-tight">
                {accountAccess.pendingTitle}
              </h2>
              <p className="mt-3 text-muted-foreground">
                Your organization{' '}
                <span className="font-medium">{organization?.name}</span> has
                been created but requires activation before you can access the
                API Dashboard.
              </p>
            </div>
          </div>

          <div className="p-6 space-y-4 rounded-lg border">
            <h3 className="text-xl font-semibold">Next step: Contact Sales</h3>
            <p>
              To activate your account, you must contact our sales team. This is
              a required step before you can access the API Dashboard.
            </p>

            {contactFormSubmitted ? (
              <div className="p-4 text-green-700 bg-green-50 rounded-md">
                {accountAccess.thankYouMessage}
              </div>
            ) : (
              <Button
                onClick={handleContactSales}
                className="mt-4 w-full h-12 text-base group"
                size="lg"
              >
                <Mail className="mr-2 w-4 h-4" />
                Contact Sales to Activate
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-2 text-center">
          <button
            onClick={handleSignOut}
            className="text-sm transition-colors text-muted-foreground hover:text-primary hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
